// CE Evidence Explorer — search across all evidence objects and trace lineage.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';

const LAYER_MAP = {
  observed_event:   { label: 'L1 Observed',      cls: 'bg-blue-900/60 text-blue-300 border-blue-700' },
  derived_event:    { label: 'L2 Derived',        cls: 'bg-emerald-900/60 text-emerald-300 border-emerald-700' },
  timeline:         { label: 'L2 Timeline',       cls: 'bg-emerald-900/40 text-emerald-200 border-emerald-800' },
  change_record:    { label: 'L2 Change Record',  cls: 'bg-teal-900/60 text-teal-300 border-teal-700' },
  command_trace:    { label: 'L2 Command Trace',  cls: 'bg-cyan-900/60 text-cyan-300 border-cyan-700' },
  incident_case:    { label: 'L2 Incident',       cls: 'bg-amber-900/60 text-amber-300 border-amber-700' },
  evidence_bundle:  { label: 'Packet',            cls: 'bg-purple-900/60 text-purple-300 border-purple-700' },
};

const ALL_TYPES = Object.keys(LAYER_MAP);

function LayerBadge({ objectType }) {
  const m = LAYER_MAP[objectType] || { label: objectType, cls: 'bg-gray-700 text-gray-300 border-gray-600' };
  return (
    <span className={`px-1.5 py-0.5 text-xs rounded border font-mono ${m.cls}`}>{m.label}</span>
  );
}

function LineageNode({ node, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className={`${depth > 0 ? 'ml-4 border-l border-gray-700 pl-3' : ''} mt-1`}>
      <div className="flex items-start gap-2 py-1">
        {hasChildren && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="text-gray-500 hover:text-gray-300 text-xs mt-0.5 w-4 shrink-0"
          >
            {expanded ? '▼' : '▶'}
          </button>
        )}
        {!hasChildren && <span className="w-4 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <LayerBadge objectType={node.object_type} />
            <span className="text-xs text-gray-300 font-mono truncate">{node.id}</span>
          </div>
          {node.label && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{node.label}</p>
          )}
        </div>
      </div>
      {expanded && hasChildren && node.children.map(child => (
        <LineageNode key={child.id} node={child} depth={depth + 1} />
      ))}
    </div>
  );
}

// Backend returns a flat { root, nodes, edges } graph, not a nested tree —
// rebuild the tree client-side from edges (from_id -> to_id) rooted at root.id.
function buildLineageTree(lineage) {
  if (!lineage?.root) return null;
  const nodeById = new Map((lineage.nodes || []).map(n => [n.id, n]));
  const childIdsByParent = new Map();
  for (const edge of lineage.edges || []) {
    if (!childIdsByParent.has(edge.from_id)) childIdsByParent.set(edge.from_id, []);
    childIdsByParent.get(edge.from_id).push(edge.to_id);
  }
  const attach = (node) => ({
    ...node,
    children: (childIdsByParent.get(node.id) || [])
      .map(id => nodeById.get(id))
      .filter(Boolean)
      .map(attach),
  });
  return attach(lineage.root);
}

function summaryText(summary) {
  if (!summary) return null;
  if (typeof summary === 'string') return summary;
  return Object.entries(summary).map(([k, v]) => `${k}: ${v}`).join(' · ');
}

function ResultRow({ item, onLineage }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-700/60 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <LayerBadge objectType={item.object_type} />
          <span className="text-xs font-mono text-gray-400">{item.object_id}</span>
        </div>
        {item.label && <p className="text-sm text-gray-200">{item.label}</p>}
        {item.summary && <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{summaryText(item.summary)}</p>}
        {item.timestamp && (
          <p className="text-xs text-gray-600 mt-0.5 font-mono">
            {new Date(item.timestamp).toLocaleString()}
          </p>
        )}
        {item.stale && (
          <span className="inline-block mt-1 px-1.5 py-0.5 text-xs rounded bg-amber-900/50 text-amber-300 border border-amber-700">
            Evidence Stale — Rebuild Required
          </span>
        )}
      </div>
      <button
        onClick={() => onLineage(item)}
        className="shrink-0 px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
      >
        Lineage
      </button>
    </div>
  );
}

export default function CEEvidenceExplorer() {
  const [searchParams] = useSearchParams();
  const [sites, setSites] = useState([]);
  // Deep-link params. `types` is what makes a category link possible at all; without it this page
  // opened with every type selected and no search run, so a link could only ever drop someone on a
  // blank form. Unknown type names are DISCARDED rather than passed through — a typo'd or renamed
  // type would otherwise produce an empty result set that looks like "no evidence exists".
  const linkedTypes = (searchParams.get('types') || '')
    .split(',').map((t) => t.trim()).filter((t) => ALL_TYPES.includes(t));
  const linkedStart = searchParams.get('start') || '';
  const linkedEnd = searchParams.get('end') || '';
  const arrivedFromLink = linkedTypes.length > 0;

  const [siteId, setSiteId] = useState(searchParams.get('site') || '');
  const [objectTypes, setObjectTypes] = useState(arrivedFromLink ? linkedTypes : ALL_TYPES);
  const [freeText, setFreeText] = useState('');
  const [startAt, setStartAt] = useState(linkedStart);
  const [endAt, setEndAt] = useState(linkedEnd);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [lineage, setLineage] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [lineageItem, setLineageItem] = useState(null);
  const lineagePanelRef = useRef(null);

  useEffect(() => {
    api.get('/ce/sites').then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  const toggleType = (t) => {
    setObjectTypes(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );
  };

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    setSearching(true);
    setResults(null);
    try {
      const body = {
        object_types: objectTypes,
        limit: 50,
      };
      if (siteId) body.site_id = siteId;
      if (freeText) body.free_text = freeText;
      if (startAt) body.start_at = new Date(startAt).toISOString();
      if (endAt) body.end_at = new Date(endAt).toISOString();
      const { data } = await api.post('/ce/search', body);
      setResults(data);
    } catch (err) {
      setResults({ error: errorDetail(err, 'Search failed') });
    } finally {
      setSearching(false);
    }
  }, [siteId, objectTypes, freeText, startAt, endAt]);

  // Run the search once on arrival from a category link. Without this the link lands on a filled-in
  // form the user still has to submit, which is not what a link promises.
  const autoRan = useRef(false);
  useEffect(() => {
    if (!arrivedFromLink || autoRan.current) return;
    autoRan.current = true;
    handleSearch();
  }, [arrivedFromLink, handleSearch]);

  const handleLineage = useCallback(async (item) => {
    setLineageItem(item);
    setLineage(null);
    setLineageLoading(true);
    setTimeout(() => lineagePanelRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      const { data } = await api.post('/ce/lineage', {
        root_object_type: item.object_type,
        root_object_id: item.object_id,
        direction: 'downstream',
        max_depth: 8,
      });
      setLineage(data);
    } catch (err) {
      setLineage({ error: errorDetail(err, 'Lineage failed') });
    } finally {
      setLineageLoading(false);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-1">Evidence Explorer</h1>
        <p className="text-sm text-gray-400 mb-6">Search evidence objects by site, type, time range, or text</p>

        {/* What this search is, and is NOT. A category link opens the evidence store filtered the
            same way the packet's category was named — it does not reproduce the packet's contents,
            and the counts legitimately differ (the generator selects by derived_at containment,
            this searches by window overlap, and the packet may have been capped). Saying so is the
            difference between a useful jumping-off point and a number that appears to contradict
            the packet. */}
        {arrivedFromLink && (
          <div
            data-testid="linked-search-notice"
            className="bg-blue-900/25 border border-blue-800 rounded-lg px-4 py-3 text-xs text-blue-200 mb-4"
          >
            Showing the evidence store for this category, site and period. This is a starting point
            for investigation — it is not the packet's contents, and the number of results here will
            not necessarily match the packet's counts.
          </div>
        )}

        {/* Search form */}
        <form onSubmit={handleSearch} className="bg-gray-800 rounded-xl border border-gray-700 p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Site</label>
              <select
                value={siteId}
                onChange={e => setSiteId(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">All sites</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>{s.site_name} ({s.site_code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Free text</label>
              <input
                type="text"
                value={freeText}
                onChange={e => setFreeText(e.target.value)}
                placeholder="Filter by label or summary..."
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Start at</label>
              <input
                type="datetime-local"
                value={startAt}
                onChange={e => setStartAt(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">End at</label>
              <input
                type="datetime-local"
                value={endAt}
                onChange={e => setEndAt(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-2">Object types</label>
            <div className="flex flex-wrap gap-2">
              {ALL_TYPES.map(t => {
                const m = LAYER_MAP[t];
                const checked = objectTypes.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    className={`px-2 py-0.5 text-xs rounded border font-mono transition-all ${
                      checked ? m.cls : 'bg-gray-700 text-gray-500 border-gray-600 opacity-50'
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={searching}
              className="px-5 py-2 text-sm rounded-lg bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white font-medium transition-colors"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
            <button
              type="button"
              onClick={() => { setSiteId(''); setFreeText(''); setStartAt(''); setEndAt(''); setObjectTypes(ALL_TYPES); setResults(null); }}
              className="px-4 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
            >
              Clear
            </button>
            {results && !results.error && (
              <span className="text-xs text-gray-500">
                {results.total_count} result{results.total_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </form>

        {/* Results */}
        {results?.error && (
          <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300 mb-4">
            {results.error}
          </div>
        )}

        {results && !results.error && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6">
            {(() => {
              const items = Object.values(results.results_by_type || {}).flat();
              return items.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No evidence objects matched your query.</p>
              ) : (
                items.map(item => (
                  <ResultRow key={`${item.object_type}:${item.object_id}`} item={item} onLineage={handleLineage} />
                ))
              );
            })()}
          </div>
        )}

        {/* Lineage panel */}
        {lineageItem && (
          <div ref={lineagePanelRef} className="bg-gray-800 rounded-xl border border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold text-white">Lineage trace</h2>
                <p className="text-xs text-gray-400 font-mono mt-0.5">
                  {lineageItem.object_type} / {lineageItem.object_id}
                </p>
              </div>
              <button
                onClick={() => { setLineage(null); setLineageItem(null); }}
                className="text-gray-500 hover:text-gray-300 text-xs"
              >
                Close
              </button>
            </div>
            {lineageLoading && <p className="text-xs text-gray-400">Loading lineage...</p>}
            {lineage?.error && (
              <p className="text-xs text-red-400">{lineage.error}</p>
            )}
            {lineage && !lineage.error && lineage.root && (
              <LineageNode node={buildLineageTree(lineage)} depth={0} />
            )}
            {lineage && !lineage.error && !lineage.root && (
              <p className="text-xs text-gray-500">No lineage data returned.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
